import { Controller } from 'react-hook-form'
import { Loader2 } from 'lucide-react'

import { InputType } from 'src/types'

import { useAlertFuelUpdateWindow } from 'src/features/AlertFuelUpdateWindow/useAlertFuelUpdateWindow'
import { AlertUpdateWindowProps } from 'src/features/AlertFuelUpdateWindow/types'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from 'src/components/shadcn/select'
import { ScrollArea } from 'src/components/shadcn/scroll-area'
import { Input } from 'src/components/shadcn/input'
import { Button } from 'src/components/shadcn/button'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/shadcn/alert-dialog'

export const AlertFuelUpdateWindow = ({
  itemId,
  isOpen,
  toggle,
  fuelQuantity,
  address,
  dateOfRefuel,
  fuelType,
  pricePerLiter,
  odometer,
  totalCost,
  distanceUnit,
  currency,
}: AlertUpdateWindowProps) => {
  const {
    fieldsLocale,
    title,
    onUpdate,
    handleSubmit,
    updateBtn,
    cancel,
    control,
    errors,
    isLoadingGeneral,
    fuelTypeOptions,
    fields,
    formattedTitle,
  } = useAlertFuelUpdateWindow({
    itemId,
    toggle,
    fuelQuantity,
    address,
    dateOfRefuel,
    pricePerLiter,
    odometer,
    totalCost,
    fuelType,
    distanceUnit,
    currency,
  })

  return (
    <form onSubmit={handleSubmit(onUpdate)}>
      <AlertDialog open={isOpen}>
        <AlertDialogContent className="max-h-[100svh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-center">{title}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            <ScrollArea className="h-[80svh] md:h-[65svh] w-full">
              {fields.map(
                ({ name, inputType, title, placeHolder, isReadOnly }) =>
                  inputType === InputType.SELECT ? (
                    <div key={name}>
                      <h1 className="mt-5 text-lg font-medium mb-2">
                        {formattedTitle(name, title)}
                      </h1>
                      <Controller
                        name={name}
                        control={control}
                        rules={{ required: fieldsLocale.requiredField }}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={placeHolder} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectLabel>{title}</SelectLabel>
                                {fuelTypeOptions.map(({ value, label }) => (
                                  <SelectItem key={value} value={value}>
                                    {label}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                  ) : (
                    <div key={name}>
                      <h1 className="mt-5 text-lg font-medium mb-2">
                        {formattedTitle(name, title)}
                      </h1>
                      <Controller
                        name={name}
                        control={control}
                        rules={{ required: fieldsLocale.requiredField }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            id={name}
                            type={inputType}
                            placeholder={placeHolder}
                            readOnly={isReadOnly}
                          />
                        )}
                      />
                      {errors[name] && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors[name]?.message}
                        </p>
                      )}
                    </div>
                  ),
              )}
            </ScrollArea>
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="rounded-[10px]"
              onClick={toggle}
              type="button"
            >
              {cancel}
            </AlertDialogCancel>
            <Button className="rounded-[10px]" onClick={onUpdate}>
              {isLoadingGeneral ? (
                <Loader2 className="animate-spin" size={13} />
              ) : (
                updateBtn
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  )
}
